import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeadSub from '../../../../components/comm/HeadSub';
import URL from '../../../../constants/url';
import BarChartBox from '../../../../components/home/steps/BarChartBox';
import { STEPS_STATUS, useHealthSteps } from '../../../../hook/useHealthSteps';
import {
    averageSteps,
    formatDisplayDate,
    formatShortDate,
    stepsToCalories,
    stepsToDistanceKm,
} from '../../../../utils/steps';
import style from './StepsDashboard.module.css';
import imgSteps from '../../../../images/common/img_3d_steps_main.png';
import imgCalendar from '../../../../images/common/i_calendar_color.svg';
import imgCalorie from '../../../../images/common/i_calorie_weight.svg';
import imgDistanc from '../../../../images/common/i_distance.svg';

/**
 * 그래프 CSS(StepsDashboard.module.css)가 nth-of-type(1)~(7) 로 막대 애니메이션 지연을
 * 하드코딩해 두었다. 일수를 바꾸려면 CSS 도 같이 손봐야 한다.
 */
const CHART_DAYS = 7;

const StepsDashboard = () => {
    const navigate = useNavigate();
    const { status, daily, message, reload } = useHealthSteps({
        days: CHART_DAYS,
    });

    // 권한이 없으면 대시보드는 보여줄 게 없다. 연동 안내 화면으로 되돌린다.
    useEffect(() => {
        if (status === STEPS_STATUS.NEED_PERMISSION) {
            navigate(URL.STEPS_MAIN, { replace: true });
        }
    }, [status, navigate]);

    const isReady = status === STEPS_STATUS.READY && daily.length > 0;

    // daily 는 과거→오늘 오름차순. 마지막이 오늘이다.
    const today = isReady ? daily[daily.length - 1] : null;
    const yesterday = isReady && daily.length > 1 ? daily[daily.length - 2] : null;

    const todaySteps = today?.steps ?? 0;
    const yesterdayDiff = todaySteps - (yesterday?.steps ?? 0);
    const weeklyAvg = averageSteps(daily);

    const chartData = daily.map((d, i) => ({
        date: formatShortDate(d.date),
        step: d.steps,
        ...(i === daily.length - 1 ? { day: '오늘' } : {}),
    }));

    if (!isReady) {
        const failed =
            status === STEPS_STATUS.ERROR || status === STEPS_STATUS.UNAVAILABLE;
        return (
            <>
                <HeadSub title="걸음수 기록" />
                <div id="sub-content" data-layout="white">
                    <div className="inner-wrap">
                        <div className={style['steps-top-box']}>
                            <div className={style['left-box']}>
                                <p>
                                    {failed
                                        ? message
                                        : '걸음수를 불러오는 중입니다…'}
                                </p>
                            </div>
                        </div>
                        {failed && (
                            <div className="bottom-fixed-box">
                                <div className="inner-box">
                                    <div className="btn-layout" data-btn-num="1">
                                        <button className="btn" data-btn-type="fill-main" data-btn-size="lg"
                                            onClick={reload}>
                                            <span>다시 시도</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <HeadSub title="걸음수 기록" />
            <div id="sub-content" data-layout="white">
                <div className={style['inner-wrap']} data-side-pad="0">

                    <div className={style['steps-top-box']}>
                        <div className={style['left-box']}>
                            <span>{formatDisplayDate(today.date)}</span>
                            <p>현재 걸음수</p>
                            <strong>{todaySteps.toLocaleString()}<span>걸음</span></strong>
                        </div>
                        <div className={style['img-box']}>
                            <img src={imgSteps} alt="" />
                        </div>
                    </div>

                    <div className={style['steps-mid-box']}>
                        <ul>
                            <li className={style['difference-steps']}>
                                <p>
                                   <img src={imgCalendar} alt="달력 아이콘" /> 어제보다
                                </p>
                                <p className={yesterdayDiff >= 0 ? style['up'] : style['down']}>
                                    <b>{Math.abs(yesterdayDiff).toLocaleString()}</b> 걸음
                                </p>
                            </li>
                            <li className={style['calorie-stps']}>
                                <p><img src={imgCalorie} alt="몸무게 아이콘" /> 소모 칼로리</p>
                                <p><b>{stepsToCalories(todaySteps)}</b> kcal</p>
                            </li>
                            <li className={style['distance-steps']}>
                                <p><img src={imgDistanc} alt="거리 아이콘" /> 걸은 거리</p>
                                <p><b>{stepsToDistanceKm(todaySteps)}</b> km</p>
                            </li>
                        </ul>
                    </div>

                    <div className={style['steps-bot-box']}>
                        <div className={style['inner-box']}>
                            <p>최근 <b>{CHART_DAYS}</b> 일 동안 </p>
                            <p> 평균 <b>{weeklyAvg.toLocaleString()}</b> 걸음을 걸었어요! </p>
                        </div>
                        <div className={style['steps-graph-box']}>
                            <div className={style['in-box']}>
                                <BarChartBox data={chartData} />
                            </div>
                        </div>
                    </div>

                    <div className="bottom-fixed-box">
                        <div className="inner-box">
                            <div className="btn-layout" data-btn-num="1">
                                <button className="btn" data-btn-type="fill-main-sub" data-btn-size="lg"
                                    onClick={() => navigate(URL.STEPS_RECORD_LIST)}>
                                    <span>전체 기록 보기</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default StepsDashboard;
